import { SmartFormRenderer } from "@smartdocs/form-renderer";
import type {
  FormDocument,
  SmartFormRendererProps,
} from "@smartdocs/form-renderer";
import Box from "@mui/material/Box";
import { useMemo, useState } from "react";

import FileReaderComponent from "../../../components/FileReaderComponent";
import LoaderWithDescription from "../../../components/LoaderWithDescription";
import getUser from "../../../utils/getUser";
import { notify } from "../../public/components/Toast";

type RendererData = {
  buttonText?: Record<string, string> | null;
  conclusionButtonText?: string;
  form: SmartFormRendererProps["formSchema"];
  isSignatureTask?: boolean;
  variables?: SmartFormRendererProps["variables"];
};

type RendererPayload = Record<string, unknown>;

export interface FormRendererProps {
  formData?: RendererData;
  processId: string;
  type?: "task" | "signature";
  taskId?: string;
  callback: (
    data: RendererPayload,
    processId: string,
    taskId?: string,
    reloadFormView?: () => void,
  ) => void | Promise<void>;
  callbackOtp?: (
    data: RendererPayload,
    processId: string,
    taskId?: string,
    reloadFormView?: () => void,
  ) => void | Promise<void>;
  cancel: () => void;
  isInitial?: boolean;
}

function FormRenderer(props: FormRendererProps): React.JSX.Element {
  const [isProcessing, setIsProcessing] = useState(false);
  const [document, setDocument] = useState<FormDocument | null>(null);
  const user = getUser();

  const buttonText = useMemo(() => {
    if (props.formData?.buttonText) return props.formData.buttonText;

    const conclusionButtonText = props.formData?.conclusionButtonText?.trim();
    if (!conclusionButtonText || conclusionButtonText === "defaultLabel") {
      return null;
    }

    return { conclusionButtonText };
  }, [props.formData?.buttonText, props.formData?.conclusionButtonText]);

  const reloadFormView = () => setIsProcessing(false);

  if (!props.formData || isProcessing) {
    return (
      <LoaderWithDescription
        title={
          props.isInitial
            ? "Iniciando a solicitação"
            : "Processando sua solicitação"
        }
        subtitle={
          props.isInitial
            ? "Por favor, aguarde enquanto o formulário é carregado"
            : "Estamos preparando a próxima etapa para você"
        }
      />
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <SmartFormRenderer
        buttonText={buttonText}
        config={{
          token: localStorage.getItem("token"),
          email: user?.email,
          taskId: props.taskId,
          processDefinitionId: props.taskId ? undefined : props.processId,
          processInstanceId: props.taskId ? props.processId : undefined,
          uploadFileUrl: `${window.location.origin}/api/v1/solicitacoes/upload`,
          getDocumentUrl: `${window.location.origin}/api/v1/files/{fileUid}/generate-temporary-link`,
          dataListUrl: `${window.location.origin}/api/v1/data-list/{dataListId}/items/all`,
        }}
        formSchema={props.formData.form}
        isSignatureTask={
          props.type === "signature" || !!props.formData.isSignatureTask
        }
        variables={props.formData.variables}
        onCancel={props.cancel}
        onOpenDocument={setDocument}
        onResendOtp={(data) =>
          props.callbackOtp?.(
            data,
            props.processId,
            props.taskId,
            reloadFormView,
          )
        }
        onSave={() =>
          notify("Atividade salva com sucesso!", "success", "")
        }
        onSubmit={async (data) => {
          setIsProcessing(true);
          await props.callback(
            data,
            props.processId,
            props.taskId,
            reloadFormView,
          );
        }}
      />

      {document && (
        <FileReaderComponent
          fileName={document.fileName}
          fileUrl={document.fileUrl}
          handleClose={() => setDocument(null)}
          open
          type={document.type}
        />
      )}
    </Box>
  );
}

export default FormRenderer;
